/** @format */

import { v4 as uuidv4 } from "uuid";
import apis from "../../utils/apis";

/**
 * Payment Module for Razorpay Integration
 * Simple, clean payment handler following the working reference pattern
 */
const usePaymentModule = ({
  amount,
  userName,
  userEmail,
  razorpayKey,
  themeColor,
  resultId,
  onSuccess,
  onFailure,
  onModalOpen,
  onModalClose,
}) => {
  // Load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Cleanup function to restore body scroll
  const cleanupRazorpay = () => {
    const restoreElementScroll = (element) => {
      if (!element) return;

      element.style.removeProperty("overflow");
      element.style.removeProperty("height");
      element.style.removeProperty("position");

      const classesToRemove = Array.from(element.classList).filter((cls) => {
        const name = cls.toLowerCase();
        return name.startsWith("razorpay") || name.startsWith("rzp");
      });

      classesToRemove.forEach((cls) => element.classList.remove(cls));
    };

    restoreElementScroll(document.body);
    restoreElementScroll(document.documentElement);

    // Remove any Razorpay backdrop/overlay
    const razorpayBackdrop = document.querySelector(".razorpay-backdrop");
    if (razorpayBackdrop) {
      razorpayBackdrop.remove();
    }

    const modalOverlay = document.querySelector(".razorpay-modal");
    if (modalOverlay) {
      modalOverlay.remove();
    }

    // Remove Razorpay container / iframe wrappers
    const razorpayContainer = document.querySelector(".razorpay-container");
    if (razorpayContainer) {
      razorpayContainer.remove();
    }

    const razorpayFrame = document.querySelector("iframe.rzp-iframe");
    if (razorpayFrame && razorpayFrame.parentElement) {
      razorpayFrame.parentElement.remove();
    }
  };

  const initiatePayment = async () => {
    const res = await loadRazorpayScript();
    if (!res) {
      alert(
        "Razorpay SDK failed to load. Please check your internet connection."
      );
      return;
    }

    let order = "";
    try {
      if (onModalOpen) onModalOpen();

      order = await apis
        .POST({
          endpoint: "/orders",
          payload: {
            amount: amount * 100,
            currency: "INR",
            receipt: uuidv4(),
          },
          options: {
            type: "json",
          },
        })
        .then((object) => {
          if (onModalClose) onModalClose();
          return object;
        });
    } catch (error) {
      if (onModalClose) onModalClose();
      console.log(error);
      return;
    }

    const options = {
      key: razorpayKey,
      amount: amount * 100,
      currency: "INR",
      name: "TrueSelfy",
      description:
        process.env.REACT_APP_ENV === "dev" ||
        process.env.REACT_APP_ENV === "test"
          ? "Top Skills Interview Questions Access Test Transaction"
          : "Top Skills Interview Questions Access",
      image: `https://www.worthysalary.trueselfy.com/assets/images/TrueSelfy-logo.svg`,
      order_id: order.data?.id,
      handler: async (res) => {
        const razorpay_payment_id = res?.razorpay_payment_id;
        // console.log("Payment successful:", razorpay_payment_id);

        // Cleanup Razorpay elements and restore scroll
        setTimeout(() => {
          cleanupRazorpay();
        }, 300);

        // Show loading modal while saving subscription
        if (onModalOpen) onModalOpen();

        try {
          const response = await apis.POST({
            endpoint: "/save-subscription-payment",
            payload: {
              ResultId: order.data?.id,
              amount: amount * 100,
              currency: "INR",
              razorpay_payment_id: razorpay_payment_id,
              date: new Date(Date.now()).toUTCString(),
              invoice: "",
            },
            options: {
              type: "json",
            },
          });

          // console.log("Subscription payment saved:", response);

          if (onSuccess) {
            onSuccess(razorpay_payment_id, res, response);
          }
        } catch (error) {
          // console.log("Subscription payment save error:", error);

          if (onFailure) {
            onFailure(error);
          }
        } finally {
          if (onModalClose) onModalClose();
        }
      },
      prefill: {
        name: userName,
        email: userEmail,
      },
      notes: {
        address: "Razorpay Corporate Office",
      },
      theme: {
        color: themeColor || "#6652E4",
      },
      modal: {
        ondismiss: function () {
          // console.log("Razorpay modal dismissed");
          // Cleanup when user closes modal
          cleanupRazorpay();
        },
        escape: true,
        backdropclose: true,
      },
    };

    const rzpay = new window.Razorpay(options);
    rzpay.open();

    rzpay.on("payment.failed", function (response) {
      response.error.amount = amount * 100;
      response.error.currency = "INR";
      response.error.resultId = resultId;

      // DO NOT cleanup on payment failure - let user see error and retry
      // Cleanup will happen when user manually closes the modal (ondismiss)

      if (onFailure) {
        onFailure(response.error);
      }

      try {
        apis
          .POST({
            endpoint: "/save-Failedpayment",
            payload: response.error,
            options: {
              type: "json",
            },
          })
          .then((object) => {
            // console.log("Failed payment saved", object);
          });
      } catch (error) {
        console.log(error);
      }
    });
  };

  return {
    initiatePayment,
  };
};

export default usePaymentModule;
